import React, { useEffect, useState, useImperativeHandle, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import { Eye, FileText, Send, Trash2, CheckCircle2, XCircle, ListOrdered, GitPullRequestArrow, Settings } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Card, CardContent } from '../../../components/ui/card';
import { Tooltip } from '../../../components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import type { DashboardProject } from '../../project/types/project';
import {
  PIPEFITTER_GET_CONFIGURATION,
  PIPEFITTER_FETCH_SEQUENCE_CONFIGURATION_LIST,
  PIPEFITTER_GENERATE_SEQUENCE_CONFIGURATION_LIST,
  PIPEFITTER_DELETE_SEQUENCE_SPREAD,
  PIPEFITTER_APPROVE_SEQUENCE_SPREAD,
  PIPEFITTER_FETCH_PIPE_LENGTH,
} from '../../../redux/Pipefitter/types';
import {
  pipefitterUpdateSpreadConfiguration,
  pipefitterUpdateSubSpreadConfiguration,
  pipefitterUpdateSpreadName,
  pipefitterPipeBasedGenerateSequence,
} from '../../../redux/Pipefitter/actions';
import { configurationConstants } from '../constants/configurationConstants';
import { commonConstants } from '../constants/commonConstants';
import { commonHelpers } from '../helpers/commonHelpers';
import { getSequenceColumns, generateSequenceData } from '../helpers/sequenceHelpers';
import { SequenceTable } from './SequenceTable';
import { SequenceView } from './SequenceView';
import { SequenceSettingsModal, type SequenceConfiguration } from './SequenceSettingsModal';
import type { Spread, SubSpread } from '../types';
import type { PipeDetailsSelectionItem } from './PipeFilter';
import type { SequenceSelectionRequest } from '../ProjectPipefitter';
import { UpdateSequenceModal } from './UpdateSequenceModal';
import { SequenceReportViewModal } from './SequenceReportViewModal';
import { PIPE_SEQUENCE_PIPES_PER_ROW } from '../constants/pipeSequenceConstants';
import type { PipeSpecificationForSequence } from './GenerateSequenceModal';

interface SequenceProps {
  project: DashboardProject;
  canEdit?: boolean;
  canReport?: boolean;
  selectionRequest?: SequenceSelectionRequest | null;
  availablePipeSelections?: PipeDetailsSelectionItem[];
  /** Project pipe size selection; aligns settings/update modals with nominal length / OD (mm). */
  pipeSpecification?: PipeSpecificationForSequence | null;
  /** Material pipe (pipefitter line); used to load full pipe status list for Update Sequence modal. */
  selectedPipe?: { pipeId?: string } | null;
}

export interface SequenceHandle {
  generateSequencePdf: () => void;
  confirmDeleteSequence: () => void;
  openSequenceSettings: () => void;
  refetchSequenceList: () => void;
}

export const Sequence = React.forwardRef<SequenceHandle, SequenceProps>(({
  project,
  canEdit = true,
  canReport = true,
  selectionRequest = null,
  availablePipeSelections = [],
  pipeSpecification = null,
  selectedPipe = null,
}, ref) => {
  const dispatch = useDispatch();
  const projectNumber = project?.project?.projectNumber;

  const configurationList = useSelector((state: any) => state.pipefitterConfiguration?.configurationList || []);
  const sequenceList = useSelector((state: any) => state.pipefitterSequence?.sequenceList || []);
  const sequenceTotalCount = useSelector(
    (state: any) => state.pipefitterSequence?.sequenceTotalCount ?? null
  );
  const sequenceLoadingMore = useSelector(
    (state: any) => state.pipefitterSequence?.sequenceLoadingMore || false
  );
  const isSequenceLoading = useSelector((state: any) => state.pipefitterSequence?.isSequenceLoading || false);
  const isConfigurationLoading = useSelector((state: any) => state.pipefitterConfiguration?.isConfigurationLoading || false);
  const pipeCounts = useSelector((state: any) => ({
    pipeCount: state.pipefitterProject?.pipeCount || 0,
    spreadPipeCount: state.pipefitterProject?.spreadPipeCount || 0,
    subSpreadPipeCount: state.pipefitterProject?.subSpreadPipeCount || 0,
    totalPipeCount: state.pipefitterProject?.totalPipeCount || 0,
    usedPipeCount: state.pipefitterProject?.usedPipeCount || 0,
  }));

  const [selectedKey, setSelectedKey] = useState<string>('0');
  const [selectionType, setSelectionType] = useState<string>('');
  const [selectedConfiguration, setSelectedConfiguration] = useState<any>({});
  const [isTableModalVisible, setIsTableModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(commonConstants.defaultPageSize);
  const [showSequenceReportModal, setShowSequenceReportModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showUpdateSequenceModal, setShowUpdateSequenceModal] = useState(false);
  const [sequenceViewMode, setSequenceViewMode] = useState<'graph' | 'table'>('graph');
  const [graphPipesPerRow, setGraphPipesPerRow] = useState(PIPE_SEQUENCE_PIPES_PER_ROW);

  const totalCount = sequenceTotalCount ?? sequenceList.length;

  const buildSequenceFetchPayload = useCallback(() => {
    if (!selectedConfiguration?.id || !selectionType || !projectNumber) return null;
    if (selectionType === configurationConstants.selectionType.spread) {
      return { projectNumber, spread: selectedConfiguration.id };
    }
    if (selectionType === configurationConstants.selectionType.subSpread) {
      return { projectNumber, subSpread: selectedConfiguration.id };
    }
    return null;
  }, [selectedConfiguration?.id, selectionType, projectNumber]);

  const refetchSequenceList = useCallback(
    (override?: { current?: number; pageSize?: number }) => {
      const params = buildSequenceFetchPayload();
      if (!params) return;
      if (override?.current != null) setCurrentPage(override.current);
      if (override?.pageSize != null) setRowsPerPage(override.pageSize);
      dispatch({
        type: PIPEFITTER_FETCH_SEQUENCE_CONFIGURATION_LIST,
        payload: params,
      });
    },
    [buildSequenceFetchPayload, dispatch]
  );

  useImperativeHandle(
    ref,
    () => ({
      generateSequencePdf: () => {
        if (!selectedConfiguration?.id || !selectionType) {
          return;
        }
        setShowSequenceReportModal(true);
      },
      confirmDeleteSequence: () => {
        if (!selectedConfiguration?.id || !selectionType) {
          return;
        }
        showDeleteModal();
      },
      openSequenceSettings: () => {
        if (!selectedConfiguration?.id || !selectionType) {
          return;
        }
        setShowSettingsModal(true);
      },
      refetchSequenceList: () => refetchSequenceList(),
    }),
    [selectedConfiguration, selectionType, refetchSequenceList]
  );

  const filteredConfigurationList = useMemo(
    () => (configurationList as Spread[]) ?? [],
    [configurationList]
  );

  const isSelectedSequenceCompleted =
    selectedConfiguration?.sequenceGenerationStatus ===
    configurationConstants.sequenceGenerationStatus.Completed;


  useEffect(() => {
    if (projectNumber) {
      dispatch({
        type: PIPEFITTER_GET_CONFIGURATION,
        payload: { projectNumber },
      });
      setSelectedKey('0');
    }
  }, [projectNumber, dispatch]);

  useEffect(() => {
    if (filteredConfigurationList?.length && projectNumber) {
      if (selectedConfiguration?.id) {
        handleClick('', {
          key: selectedKey,
          id: selectedConfiguration?.id,
          selectionType: selectionType,
        });
      } else {
        handleClick('', {
          key: '0',
          id: filteredConfigurationList[0]?.id,
          selectionType: configurationConstants.selectionType.spread,
        });
      }
    }
  }, [filteredConfigurationList, projectNumber]);

  const resetConfigurationSelection = () => {
    setSelectedConfiguration({});
    setSelectionType('');
    setCurrentPage(1);
    setRowsPerPage(commonConstants.defaultPageSize);
  };

  const selectConfiguration = ({ type, configurationId }: { type: string; configurationId: number }) => {
    setSelectionType(type);
    if (type === configurationConstants.selectionType.spread) {
      const selectedSpread = filteredConfigurationList.find((spread: Spread) => spread.id === configurationId);
      if (selectedSpread) {
        const updatedSpread = {
          ...selectedSpread,
          canGenerateSpread: true,
          hasMoreSubSpreads: (selectedSpread.subSpreads?.length ?? 0) > 0,
        };
        setSelectedConfiguration(updatedSpread);

        if (
          selectedSpread.sequenceGenerationStatus ===
          configurationConstants.sequenceGenerationStatus.Completed
        ) {
          setCurrentPage(1);
          dispatch({
            type: PIPEFITTER_FETCH_SEQUENCE_CONFIGURATION_LIST,
            payload: {
              projectNumber: projectNumber,
              spread: selectedSpread.id,
            },
          });
        }
      }
    } else if (type === configurationConstants.selectionType.subSpread) {
      const allSubSpreads: SubSpread[] = [];
      filteredConfigurationList.forEach((spread: Spread) => {
        spread.subSpreads?.forEach((sub: SubSpread) => allSubSpreads.push(sub));
      });

      const selectedSubSpread = allSubSpreads.find((ite: SubSpread) => ite.id === configurationId);
      if (selectedSubSpread) {
        const updatedSubSpread = {
          ...selectedSubSpread,
          canGenerateSpread: true,
        };
        setSelectedConfiguration(updatedSubSpread);

        if (
          selectedSubSpread.sequenceGenerationStatus === configurationConstants.sequenceGenerationStatus.Completed
        ) {
          setCurrentPage(1);

          dispatch({
            type: PIPEFITTER_FETCH_SEQUENCE_CONFIGURATION_LIST,
            payload: {
              projectNumber: projectNumber,
              subSpread: selectedSubSpread.id,
            },
          });
        }
      }
    }
  };

  const handleClick = (e: any, info: any) => {
    setSelectedKey(info.key);
    resetConfigurationSelection();
    selectConfiguration({
      type: info.selectionType,
      configurationId: info.id,
    });

    // Fetch pipe length
    if (info.selectionType === configurationConstants.selectionType.spread) {
      dispatch({
        type: PIPEFITTER_FETCH_PIPE_LENGTH,
        payload: {
          projectNumber,
          spreadId: info.id,
          subSpreadId: '0',
        },
      });
    } else {
      const spread = configurationList.find((s: Spread) =>
        s.subSpreads?.some((sub: SubSpread) => sub.id === info.id)
      );
      if (spread) {
        dispatch({
          type: PIPEFITTER_FETCH_PIPE_LENGTH,
          payload: {
            projectNumber,
            spreadId: spread.id,
            subSpreadId: info.id,
          },
        });
      }
    }
  };

  useEffect(() => {
    if (!selectionRequest?.id) return;
    if (!filteredConfigurationList?.length) return;
    if (selectionRequest.selectionType === 'spread') {
      const spreadIndex = filteredConfigurationList.findIndex((spread: Spread) => spread.id === selectionRequest.id);
      if (spreadIndex >= 0) {
        handleClick('', {
          key: `${spreadIndex}`,
          id: selectionRequest.id,
          selectionType: configurationConstants.selectionType.spread,
        });
      }
      return;
    }
    for (let index = 0; index < filteredConfigurationList.length; index += 1) {
      const spread = filteredConfigurationList[index];
      const subIndex = (spread.subSpreads || []).findIndex((sub: SubSpread) => sub.id === selectionRequest.id);
      if (subIndex >= 0) {
        handleClick('', {
          key: `${index}-${subIndex}`,
          id: selectionRequest.id,
          selectionType: configurationConstants.selectionType.subSpread,
        });
        break;
      }
    }
  }, [selectionRequest?.token, filteredConfigurationList]);

  const generateSequence = () => {
    let payload: any = {};
    if (selectionType === configurationConstants.selectionType.spread) {
      payload = {
        generateFor: configurationConstants.selectionType.spread,
        projectNumber: projectNumber,
        spreadId: selectedConfiguration.id?.toString(),
      };
    } else if (selectionType === configurationConstants.selectionType.subSpread) {
      payload = {
        generateFor: configurationConstants.selectionType.subSpread,
        projectNumber: projectNumber,
        subSpreadId: selectedConfiguration.id?.toString(),
      };
    }

    dispatch({
      type: PIPEFITTER_GENERATE_SEQUENCE_CONFIGURATION_LIST,
      payload: payload,
      cb: () => {
        dispatch({
          type: PIPEFITTER_GET_CONFIGURATION,
          payload: { projectNumber },
          cb: () => {
            refetchSequenceList({ current: 1, pageSize: commonConstants.defaultPageSize });
          },
        });
      },
      error: () => {},
    });
  };

  const deleteSpreadSubSpread = () => {
    let payload: any = {
      projectNumber: projectNumber,
      sequenceDeletionFor: selectionType,
    };
    if (selectionType === configurationConstants.selectionType.spread) {
      payload = {
        ...payload,
        spreadId: selectedConfiguration.id?.toString(),
      };
    }

    if (selectionType === configurationConstants.selectionType.subSpread) {
      payload = {
        ...payload,
        subSpreadId: selectedConfiguration.id?.toString(),
      };
    }

    dispatch({
      type: PIPEFITTER_DELETE_SEQUENCE_SPREAD,
      payload: payload,
      cb: () => {
        dispatch({
          type: PIPEFITTER_GET_CONFIGURATION,
          payload: { projectNumber },
          cb: () => {
            setCurrentPage(1);
            setRowsPerPage(commonConstants.defaultPageSize);
          },
        });
      },
      error: () => {},
    });
  };

  const approveSequenceSpread = () => {
    if (selectedConfiguration?.sequenceApprovalStatus === 'Approved') {
      return;
    }

    let params: any = {};
    if (selectionType === configurationConstants.selectionType.spread) {
      params = {
        generateFor: configurationConstants.selectionType.spread,
        projectNumber: projectNumber,
        spreadId: selectedConfiguration?.id?.toString(),
      };
    } else if (selectionType === configurationConstants.selectionType.subSpread) {
      params = {
        generateFor: configurationConstants.selectionType.subSpread,
        projectNumber: projectNumber,
        subSpreadId: selectedConfiguration?.id?.toString(),
      };
    }

    dispatch({
      type: PIPEFITTER_APPROVE_SEQUENCE_SPREAD,
      payload: params,
      cb: () => {
        dispatch({
          type: PIPEFITTER_GET_CONFIGURATION,
          payload: { projectNumber },
          cb: () => {
            setCurrentPage(1);
            setRowsPerPage(commonConstants.defaultPageSize);
          },
        });
      },
      error: () => {},
    });
  };

  const handleTableChange = (pagination: { current: number; pageSize: number }) => {
    setCurrentPage(pagination.current);
    setRowsPerPage(pagination.pageSize);
  };

  const showDeleteModal = () => {
    let deleteMessage = '';
    if (
      selectionType === configurationConstants.selectionType.spread ||
      selectedConfiguration?.subSpreads?.length === 0
    ) {
      deleteMessage = 'Delete Sequence?';
    } else if (selectionType === configurationConstants.selectionType.subSpread) {
      deleteMessage = 'Delete Sequence?';
    }
    Modal.confirm({
      title: deleteMessage,
      okText: 'Yes',
      cancelText: 'No',
      width: 600,
      onOk: deleteSpreadSubSpread,
    });
  };

  const showApproveModal = () => {
    Modal.confirm({
      title: 'Approve Sequence?',
      okText: 'Yes',
      cancelText: 'No',
      width: 500,
      onOk: approveSequenceSpread,
    });
  };

  const spreadName = () => {
    if (selectionType === configurationConstants.selectionType.spread) {
      return selectedConfiguration.spreadName;
    }
    return '';
  };

  const isSequenceApproved = selectedConfiguration?.sequenceApprovalStatus === 'Approved';

  if (isConfigurationLoading && (!filteredConfigurationList || filteredConfigurationList.length === 0)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]" />
          <span className="text-gray-600 text-lg">Loading configuration...</span>
        </div>
      </div>
    );
  }

  // No configuration: use same left/right layout with empty spreads list and message on the right
  if (!filteredConfigurationList || filteredConfigurationList.length === 0) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="flex min-h-[480px]">
            <div className="w-[15%] shrink-0 border-r border-[#E2E5EA] bg-page-bg">
              <div className="p-4">
                <div className="space-y-2">
                  <div className="text-[11px] font-semibold text-heritage-grey uppercase tracking-[0.06em] px-2 pb-2">
                    Sequences
                  </div>
                  <div className="rounded p-2 text-sm text-muted-foreground">No sequences</div>
                </div>
              </div>
            </div>
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="flex flex-col items-center justify-center text-center max-w-sm">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <ListOrdered className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No sequences</h3>
                  <p className="text-gray-500 text-sm max-w-sm">
                          Add configuration to create sequences. Select a sequence from the left to view and manage sequence pipes in the table.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-0">
          <div className="flex min-h-[480px]">
            <div className="w-[15%] min-w-0 shrink-0 border-r border-[#E2E5EA] bg-page-bg">
              <div className="p-4">
                <div className="space-y-2">
                  {commonHelpers.sortBySortOrder(filteredConfigurationList as Spread[])?.map((spread: Spread, index: number) => (
                    <div key={spread.id} className="space-y-1 min-w-0">
                      <div
                        className={`min-w-0 p-2 rounded-lg cursor-pointer text-[13px] ${
                          selectedKey === `${index}`
                            ? 'bg-hero-red/15 text-blueprint-blue font-medium'
                            : 'text-text-grey hover:bg-white'
                        }`}
                        onClick={() =>
                          handleClick('', {
                            key: `${index}`,
                            id: spread.id,
                            selectionType: configurationConstants.selectionType.spread,
                          })
                        }
                      >
                        <div className="text-xs leading-snug break-words whitespace-normal">{spread.spreadName}</div>
                      </div>
                      {spread.subSpreads?.map((subSpread: SubSpread, subIndex: number) => (
                        <div
                          key={subSpread.id}
                          className={`ml-4 p-2 rounded-lg cursor-pointer text-[12px] ${
                            selectedKey === `${index}-${subIndex}`
                              ? 'bg-hero-red/15 text-blueprint-blue font-medium'
                              : 'text-text-grey hover:bg-white'
                          }`}
                          onClick={() =>
                            handleClick('', {
                              key: `${index}-${subIndex}`,
                              id: subSpread.id,
                              selectionType: configurationConstants.selectionType.subSpread,
                            })
                          }
                        >
                          <div className="text-sm">{subSpread.subSpreadName}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex-1 flex flex-col min-w-0">
              {/* <div className="flex items-center justify-between gap-2 p-4 border-b border-border shrink-0">
                <div className="flex items-center gap-2">
                  {sequenceList.length > 0 && (
                    <>
                      {canEdit && (
                        <>
                          <Tooltip content="View Sequence">
                            <Button variant="ghost" size="icon" onClick={() => setIsTableModalVisible(true)} aria-label="View sequence">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Sequence report">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setShowSequenceReportModal(true)}
                              aria-label="Sequence report"
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </>
                      )}
                      {canEdit && (
                        <Tooltip content="Send Email">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setShowEmailModal(true)}
                            disabled={isSendingEmail}
                            aria-label="Send email"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </Tooltip>
                      )}
                      {canEdit && (
                        <Tooltip content="Delete Sequence">
                          <Button variant="ghost" size="icon" onClick={showDeleteModal} aria-label="Delete sequence">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </Tooltip>
                      )}
                      {canEdit && (
                        <Tooltip content={isSequenceApproved ? 'Approved' : 'Approve Sequence'}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={isSequenceApproved ? undefined : showApproveModal}
                            disabled={isSequenceApproved}
                            aria-label={isSequenceApproved ? 'Approved' : 'Approve sequence'}
                          >
                            {isSequenceApproved ? (
                              <CheckCircle2 className="h-4 w-4 text-secondary-green" />
                            ) : (
                              <XCircle className="h-4 w-4" />
                            )}
                          </Button>
                        </Tooltip>
                      )}
                    </>
                  )}
                </div>
              </div> */}

              <div className="flex-1 p-4 overflow-auto">
                {selectedConfiguration?.id && selectedConfiguration?.canGenerateSpread && (!isSequenceLoading || sequenceList.length > 0) ? (
                  isSelectedSequenceCompleted ? (
                    <SequenceTable
                      sequenceList={sequenceList}
                      hasMoreSubSpreads={selectedConfiguration.hasMoreSubSpreads}
                      selectionType={selectionType}
                      selectedConfiguration={selectedConfiguration}
                      currentPage={currentPage}
                      rowsPerPage={rowsPerPage}
                      totalCount={totalCount}
                      handleTableChange={handleTableChange}
                      generateSequence={generateSequence}
                      doesHavePermissionToGenerate={canEdit}
                      doesHavePermissionToViewApprove={canEdit}
                      isSequenceApproved={isSequenceApproved}
                      projectNumber={projectNumber}
                      pipeCounts={pipeCounts}
                      onOpenSequenceReport={() => setShowSequenceReportModal(true)}
                      onOpenUpdateSequence={() => setShowUpdateSequenceModal(true)}
                      onOpenSequenceSettings={() => setShowSettingsModal(true)}
                      canReport={canReport}
                      sequenceViewMode={sequenceViewMode}
                      onSequenceViewModeChange={setSequenceViewMode}
                      graphRecordList={sequenceList}
                      graphPipesPerRow={graphPipesPerRow}
                      onGraphPipesPerRowChange={setGraphPipesPerRow}
                      isGraphPanelLoading={isSequenceLoading && sequenceList.length === 0}
                      sequenceLoadingMore={sequenceLoadingMore}
                      graphFetchError={null}
                      onRetryGraphFetch={() => refetchSequenceList()}
                    />
                  ) : (
                    <div className="flex items-center justify-center min-h-[360px]">
                      <div className="flex flex-col items-center justify-center text-center max-w-md">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <ListOrdered className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue mb-2">
                          Sequence generation in progress
                        </h3>
                        <p className="text-[13px] text-heritage-grey">
                          The sequence is still being generated. Select another sequence or check back shortly.
                        </p>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="flex items-center justify-center min-h-[360px]">
                    {!selectedConfiguration?.id ? (
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <ListOrdered className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue mb-2">
                          Select a sequence
                        </h3>
                        <p className="text-[13px] text-heritage-grey max-w-sm">
                          Select a sequence from the left panel to view and manage sequence pipes.
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]" />
                        <span className="text-gray-600 text-lg">Loading sequences...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isTableModalVisible} onOpenChange={setIsTableModalVisible}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Sequence View</DialogTitle>
          </DialogHeader>
          {selectedConfiguration?.id && (
            <SequenceView
              sequenceList={sequenceList}
              selectionType={selectionType}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              handleTableChange={handleTableChange}
              spreadName={spreadName()}
              onClose={() => setIsTableModalVisible(false)}
              canEdit={canEdit}
            />
          )}
        </DialogContent>
      </Dialog>

      <UpdateSequenceModal
        open={showUpdateSequenceModal}
        onOpenChange={setShowUpdateSequenceModal}
        dispatch={dispatch}
        projectNumber={projectNumber}
        projectId={project?.project?.projectId}
        projectName={project?.project?.projectName ?? ''}
        materialPipeId={selectedPipe?.pipeId}
        selectionType={selectionType}
        selectedConfiguration={selectedConfiguration}
        filteredConfigurationList={filteredConfigurationList as Spread[]}
        sequenceList={sequenceList}
        isSubmitting={isSequenceLoading}
        pipeSpecification={pipeSpecification}
        onSuccess={() =>
          dispatch({
            type: PIPEFITTER_GET_CONFIGURATION,
            payload: { projectNumber },
          })
        }
      />

      {showEmailModal && (
        <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Email</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Email functionality will be implemented with Reports enhancements.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEmailModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <SequenceSettingsModal
        open={showSettingsModal}
        onOpenChange={setShowSettingsModal}
        configuration={selectedConfiguration?.id ? (selectedConfiguration as SequenceConfiguration) : null}
        selectionType={
          selectionType === configurationConstants.selectionType.subSpread ? 'SubSpread' : 'Spread'
        }
        spreadId={
          selectionType === configurationConstants.selectionType.spread
            ? selectedConfiguration?.id
            : selectedConfiguration?.spreadId ?? filteredConfigurationList.find((s: Spread) =>
                s.subSpreads?.some((sub: SubSpread) => sub.id === selectedConfiguration?.id)
              )?.id
        }
        projectNumber={projectNumber ?? undefined}
        subSpreadId={
          selectionType === configurationConstants.selectionType.subSpread ? selectedConfiguration?.id : undefined
        }
        pipeSpecification={pipeSpecification}
        sequencePipeCount={sequenceList.length > 0 ? sequenceList.length : undefined}
        loading={isConfigurationLoading}
        onSaveNameOnly={({ spreadId, projectNumber: pn, sequenceName }) => {
          dispatch(
            pipefitterUpdateSpreadName(
              { spreadId, projectNumber: pn, sequenceName },
              () => {
                setShowSettingsModal(false);
                dispatch({
                  type: PIPEFITTER_GET_CONFIGURATION,
                  payload: { projectNumber: pn },
                  cb: () => {
                    setCurrentPage(1);
                    setRowsPerPage(commonConstants.defaultPageSize);
                    resetConfigurationSelection();
                  },
                });
              },
              () => {}
            )
          );
        }}
        onSaveRegenerate={(settings) => {
          if (!selectedConfiguration?.id || !projectNumber) return;
          const sequenceName =
            (settings.spreadName ?? settings.subSpreadName ?? settings.sequenceName ?? '')?.toString().trim() || '';
          const existingSequenceRef =
            selectionType === configurationConstants.selectionType.spread
              ? { spreadId: String(selectedConfiguration.id) }
              : { subSpreadId: String(selectedConfiguration.id) };
          const config: Record<string, unknown> = {
            targetNumberOfPipes: Number(settings.targetNumberOfPipes),
          };
          if (settings.nominalPipeLengthFt != null) config.nominalPipeLengthFt = Number(settings.nominalPipeLengthFt);
          if (settings.targetPipelineLengthFt != null) config.targetPipelineLengthFt = Number(settings.targetPipelineLengthFt);
          if (settings.nominalODMm != null) config.nominalODmm = Number(settings.nominalODMm);
          if (settings.exclusion6OClockMm != null) config.exclusion6OClockMm = Number(settings.exclusion6OClockMm);
          if (settings.weldSeamExclusionMm != null) config.weldSeamExclusionMm = Number(settings.weldSeamExclusionMm);
          if (settings.hiloThresholdMm != null) config.hiLoThresholdMm = Number(settings.hiloThresholdMm);
          if (settings.pipeMaterial != null) config.pipeMaterial = String(settings.pipeMaterial).trim();
          config.pipeType = settings.pipeType === 'double' ? 'Double' : 'Single';
          config.pipeRotation =
            settings.pipeRotation === 'clockwise'
              ? 'Clockwise'
              : settings.pipeRotation === 'antiClockwise'
                ? 'AntiClockwise'
                : 'Any';
          config.fitQuality = settings.fitQuality === 'acceptable' ? 'Acceptable' : 'Optimal';
          config.allowPipeTranslation = Boolean(settings.allowPipeTranslation);

          const payload = {
            projectNumber,
            mode: 'REGENERATE',
            sequenceName,
            existingSequenceRef,
            config,
          };
          dispatch(
            pipefitterPipeBasedGenerateSequence(
              payload,
              () => {
                setShowSettingsModal(false);
                dispatch({
                  type: PIPEFITTER_GET_CONFIGURATION,
                  payload: { projectNumber },
                  cb: () => {
                    setCurrentPage(1);
                    setRowsPerPage(commonConstants.defaultPageSize);
                    resetConfigurationSelection();
                  },
                });
              },
              () => {}
            )
          );
        }}
        onSaveConfiguration={async (settings) => {
          if (!selectedConfiguration?.id || !projectNumber) return;
          const payload: Record<string, unknown> = {
            ...settings,
            projectNumber,
          };
          if (selectionType === configurationConstants.selectionType.spread) {
            payload.spreadId = selectedConfiguration.id;
            dispatch(pipefitterUpdateSpreadConfiguration(payload));
          } else {
            payload.subSpreadId = selectedConfiguration.id;
            dispatch(pipefitterUpdateSubSpreadConfiguration(payload));
          }
          dispatch({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber } });
        }}
        onDeleteSequence={() => {
          if (!selectedConfiguration?.id || !selectionType) return;
          const payload: any = {
            projectNumber,
            sequenceDeletionFor: selectionType,
          };
          if (selectionType === configurationConstants.selectionType.spread) {
            payload.spreadId = selectedConfiguration.id?.toString();
          } else {
            payload.subSpreadId = selectedConfiguration.id?.toString();
          }
          dispatch({
            type: PIPEFITTER_DELETE_SEQUENCE_SPREAD,
            payload,
            cb: () => {
              setShowSettingsModal(false);
              dispatch({
                type: PIPEFITTER_GET_CONFIGURATION,
                payload: { projectNumber },
                cb: () => {
                  setCurrentPage(1);
                  setRowsPerPage(commonConstants.defaultPageSize);
                  resetConfigurationSelection();
                },
              });
            },
            error: () => {},
          });
        }}
      />

      <SequenceReportViewModal
        isOpen={showSequenceReportModal}
        onClose={() => setShowSequenceReportModal(false)}
        projectNumber={projectNumber ?? ''}
        projectName={project?.project?.projectName ?? ''}
        selectionType={selectionType}
        selectedConfiguration={selectedConfiguration}
        canReport={canReport}
      />
    </>
  );
});
